/**
 * HitJuice — per-hit impact parameters.
 * Pure module: no Phaser, no DOM. Testable in node.
 */

/** Minimum hitstop frames (lightest hits). */
export const HITSTOP_MIN = 3;
/** Maximum hitstop frames (heaviest hits). */
export const HITSTOP_MAX = 8;
/** Damage at which the white-flash overlay fires. */
export const HEAVY_HIT_THRESHOLD = 14;
/** Damage at which speed-lines trigger (super-class moves). */
export const SUPER_HIT_THRESHOLD = 20;

// ── Gib / gore system ────────────────────────────────────────────────────────

/** Blood/gore fidelity level set in options. */
export type BloodLevel = 'off' | 'normal' | 'gore';

/**
 * Context passed to gib-gate check: was the hit a KO, a super, or a launcher-
 * finisher? All three must be available to determine whether gibs fire.
 */
export interface GibTrigger {
  isKO: boolean;
  isSuper: boolean;
  /** True when the killing hit had HitSpec.launcher === true (heavy finisher). */
  isFinisher: boolean;
}

/**
 * True when the over-the-top gib burst should play.
 * Rules: gore mode only; must be a KO; must be a super OR a heavy finisher.
 * Normal hits never gib — even in gore mode.
 */
export function shouldPlayGibs(bloodLevel: BloodLevel, trig: GibTrigger): boolean {
  if (bloodLevel !== 'gore') return false;
  if (!trig.isKO) return false;
  return trig.isSuper || trig.isFinisher;
}

/** Chunky cel-gib particles to spawn (0 unless gore is active). */
export function gibParticleCount(bloodLevel: BloodLevel): number {
  return bloodLevel === 'gore' ? 20 : 0;
}

/** Extra hitstop frames added on a gib KO (stacks on normal hitstop). */
export const GIB_HITSTOP_BONUS = 4;

/** Extra camera shake amplitude (px) on a gib KO. */
export const GIB_SHAKE_BONUS = 4;

/** Weakest light-attack damage — used as scale baseline. */
const DAMAGE_BASELINE = 6;
/** Damage span over which hitstop ramps from MIN to MAX. */
const DAMAGE_RANGE = 20;

/**
 * Hitstop frames for a hit of the given damage.
 * Returns a value clamped to [HITSTOP_MIN, HITSTOP_MAX].
 */
export function hitstopFrames(damage: number): number {
  const t = Math.min(Math.max(damage - DAMAGE_BASELINE, 0) / DAMAGE_RANGE, 1);
  return Math.round(HITSTOP_MIN + t * (HITSTOP_MAX - HITSTOP_MIN));
}

/**
 * Camera shake amplitude (px) for a hit of the given damage.
 * Returns 0 for very light hits; scales up for heavy ones, capped at 8 px.
 */
export function shakeAmplitude(damage: number): number {
  if (damage < DAMAGE_BASELINE) return 0;
  return Math.min((damage - DAMAGE_BASELINE) * 0.4, 8);
}

/** True when damage reaches the heavy-hit threshold (white flash fires). */
export function isHeavyHit(damage: number): boolean {
  return damage >= HEAVY_HIT_THRESHOLD;
}

/** True when damage is super-class (speed-lines fire). */
export function isSuperHit(damage: number): boolean {
  return damage >= SUPER_HIT_THRESHOLD;
}

/**
 * Number of blood-spatter dots to spawn.
 * Returns 0 when blood is toggled off in options.
 */
export function bloodParticleCount(damage: number, bloodEnabled: boolean): number {
  if (!bloodEnabled) return 0;
  if (damage < 8) return 3;
  if (damage < 16) return 6;
  return 10;
}

// ── Blood/gore FX — hit spray + KO pool ─────────────────────────────────────

/** Cap on lingering blood-pool decals; oldest removed when limit is reached. */
export const MAX_BLOOD_DECALS = 12;

/** Particle multiplier applied in gore mode on top of the base count. */
export const GORE_PARTICLE_MULTIPLIER = 2;

/** Weapon IDs classified as blade weapons (spray more particles). */
const BLADE_WEAPON_IDS = new Set<string>(['knife']);

/** True when the weapon ID belongs to a blade-class weapon. */
export function isBladeWeaponId(weaponId?: string): boolean {
  return weaponId !== undefined && BLADE_WEAPON_IDS.has(weaponId);
}

/**
 * Directional blood-spray particle count for a hit.
 * Uses BloodLevel for richer semantics: `off` → 0, `gore` → doubled.
 * Blade weapons add 50 % extra spray.
 */
export function hitSprayCount(damage: number, level: BloodLevel, isBladeWeapon = false): number {
  if (level === 'off') return 0;
  let base: number;
  if (damage < 8) base = 3;
  else if (damage < 16) base = 6;
  else base = 10;
  if (isBladeWeapon) base = Math.round(base * 1.5);
  if (level === 'gore') base = Math.round(base * GORE_PARTICLE_MULTIPLIER);
  return base;
}

/**
 * Width (px) of the blood-pool decal placed under a KO'd body.
 * Returns 0 when blood is off or the hit is too light to leave a pool.
 * Gore level widens the pool by 50 %.
 */
export function koPoolWidth(damage: number, level: BloodLevel): number {
  if (level === 'off') return 0;
  if (damage < 8) return 0;
  const base = damage < 16 ? 24 : 40;
  return level === 'gore' ? Math.round(base * 1.5) : base;
}
